import React, { useEffect, useRef, useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import { useTranslation } from 'react-i18next';

interface CopyCallIdButtonProps {
  callId?: string | null;
  showLabel?: boolean;
}

export const CopyCallIdButton: React.FC<CopyCallIdButtonProps> = ({
  callId,
  showLabel = false,
}) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsCopied(false);
  }, [callId]);

  if (!callId) return null;

  const feedbackKey = isCopied ? 'webSocketCall.copied' : 'webSocketCall.copyCallId';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(callId);
      setIsCopied(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
        timeoutRef.current = null;
      }, 1500);
    } catch {
      setIsCopied(false);
    }
  };

  const icon = isCopied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />;

  return (
    <Tooltip title={t(feedbackKey)} arrow>
      {showLabel ? (
        <Button
          onClick={handleCopy}
          aria-label={t(feedbackKey)}
          size="small"
          variant="outlined"
          startIcon={icon}>
          {t(feedbackKey)}
        </Button>
      ) : (
        <IconButton onClick={handleCopy} aria-label={t(feedbackKey)} size="small">
          {icon}
        </IconButton>
      )}
    </Tooltip>
  );
};
